/** Etapas internas da expedição (D-14.3). */
export const FULFILLMENT_STAGES = [
    "separation",
    "checking",
    "packing",
    "dispatch",
    "delivered",
    "reverse"
];
export const FULFILLMENT_STAGE_LABELS = {
    separation: "Separação",
    checking: "Conferência",
    packing: "Embalagem",
    dispatch: "Despacho",
    delivered: "Entregue",
    reverse: "Logística reversa"
};
/**
 * Tipos de ocorrência que travam/anotam a fila (D-14.3). Vocabulário fechado
 * para a fila poder filtrar e priorizar; o relato livre vai na nota.
 */
export const OCCURRENCE_KINDS = ["delay", "damage", "address_issue", "recipient_absent", "lost"];
export const OCCURRENCE_KIND_LABELS = {
    delay: "Atraso",
    damage: "Avaria/dano no transporte",
    address_issue: "Endereço incorreto/incompleto",
    recipient_absent: "Destinatário ausente",
    lost: "Objeto extraviado"
};
/**
 * Transições permitidas entre etapas.
 *
 * Escolhas que merecem explicação (D-14.5):
 * - **A linha principal é sequencial** (`separation → checking → packing →
 *   dispatch → delivered`): conferir antes de embalar e embalar antes de
 *   despachar é o que a ocorrência de avaria/endereço existe para proteger.
 *   Pular etapa apagaria a conferência que justifica o despacho.
 * - **`reverse` (logística reversa) só sai de `dispatch` ou `delivered`**: só
 *   faz sentido coletar de volta o que já saiu. Antes do despacho, o certo é
 *   não despachar (a mercadoria nunca saiu), não "reverter".
 * - **Estados finais**: `delivered` ainda pode ir para `reverse` (devolução
 *   após a entrega); `reverse` é terminal (a etapa logística reversa concluída
 *   encerra a ordem de trabalho — o tratamento de garantia é o RMA da Task 15).
 */
export const ALLOWED_STAGE_TRANSITIONS = {
    separation: ["checking"],
    checking: ["packing"],
    packing: ["dispatch"],
    dispatch: ["delivered", "reverse"],
    delivered: ["reverse"],
    reverse: []
};
/** Etapas finais: nada sai delas. */
export const TERMINAL_STAGES = FULFILLMENT_STAGES.filter((stage) => ALLOWED_STAGE_TRANSITIONS[stage].length === 0);
export function isFulfillmentStage(value) {
    return typeof value === "string" && FULFILLMENT_STAGES.includes(value);
}
export function isOccurrenceKind(value) {
    return typeof value === "string" && OCCURRENCE_KINDS.includes(value);
}
export function isTerminalStage(stage) {
    return ALLOWED_STAGE_TRANSITIONS[stage].length === 0;
}
export function nextStages(stage) {
    return ALLOWED_STAGE_TRANSITIONS[stage];
}
export function canAdvance(from, to) {
    return ALLOWED_STAGE_TRANSITIONS[from].includes(to);
}
/** Campos obrigatórios por etapa de destino. Etapa ausente = nada exigido. */
export const FULFILLMENT_STAGE_REQUIREMENTS = {
    dispatch: ["carrier", "trackingCode"],
    reverse: ["reason"]
};
export const FULFILLMENT_STAGE_FIELD_LABELS = {
    carrier: "Transportadora",
    trackingCode: "Código de rastreio",
    reason: "Motivo da reversa"
};
export function fulfillmentRequirementsFor(to) {
    return FULFILLMENT_STAGE_REQUIREMENTS[to] ?? [];
}
/** Campos exigidos pela transição que não vieram preenchidos. */
export function missingFulfillmentRequirements(to, input = {}) {
    return fulfillmentRequirementsFor(to).filter((field) => {
        const value = input[field];
        return typeof value !== "string" || value.trim() === "";
    });
}
/**
 * Única porta de validação de transição de etapa — usada pelo servidor (antes
 * de gravar) e pela tela (antes de oferecer). Devolve o motivo da recusa em
 * vez de lançar: quem chama precisa dizer ao operador O QUE falta.
 */
export function checkFulfillmentStageTransition(from, to, input = {}) {
    if (!isFulfillmentStage(to))
        return { ok: false, error: "unknown_stage", fields: ["to"] };
    if (!canAdvance(from, to))
        return { ok: false, error: "invalid_stage_transition", fields: [from, to] };
    const missing = missingFulfillmentRequirements(to, input);
    if (missing.length)
        return { ok: false, error: "stage_requirements_missing", fields: missing };
    return { ok: true };
}
export function checkOccurrence(kind, note) {
    if (!isOccurrenceKind(kind))
        return { ok: false, error: "unknown_occurrence_kind", fields: ["occurrenceKind"] };
    if (typeof note !== "string" || note.trim() === "") {
        return { ok: false, error: "occurrence_note_required", fields: ["occurrenceNote"] };
    }
    return { ok: true };
}
// ---------------------------------------------------------------------------
// Visão unificada: estado INTERNO (esta máquina) + estado do TRANSPORTADOR
// (rastreio, `order_tracking_events`/integration-tracking). O brief exige a
// visão unificada mesmo quando a execução é externa (D-14.4): a tela mostra os
// dois lados lado a lado, e esta função declara quando eles DIVERGEM em vez de
// escolher um e esconder o outro.
// ---------------------------------------------------------------------------
/** O que o estado interno espera do rastreio externo, para comparar. */
const STAGE_EXPECTED_TRACKING = {
    // Antes do despacho o transportador ainda não tem o objeto: confirmado é o
    // esperado, mas "nada ainda" também não é divergência.
    separation: ["confirmed"],
    checking: ["confirmed"],
    packing: ["confirmed"],
    dispatch: ["shipped", "in_transit"],
    delivered: ["delivered"],
    // Na reversa o objeto pode estar em trânsito de volta ou ter dado exceção.
    reverse: ["in_transit", "exception", "delivered"]
};
/**
 * Mescla o estado interno com o rastreio externo e CLASSIFICA a relação entre
 * os dois. Puro e determinístico: a tela e o teste leem a mesma classificação.
 *
 * - `tracking_missing`: ainda não há evento do transportador — não é erro, é
 *   "nada postado ainda"; a tela diz isso em vez de fingir alinhamento.
 * - `tracking_exception`: o transportador reportou exceção (avaria/extravio) —
 *   a fila precisa ver isso mesmo que a etapa interna não tenha mudado.
 * - `tracking_ahead`/`internal_ahead`: os dois lados discordam de direção; a
 *   tela mostra ambos e sinaliza a divergência, nunca apaga um.
 */
export function mergeFulfillmentView(stage, trackingStatus) {
    if (trackingStatus === null) {
        return {
            stage,
            trackingStatus: null,
            sync: "tracking_missing",
            note: "Sem evento do transportador ainda — o estado interno segue como fonte."
        };
    }
    if (trackingStatus === "exception") {
        return {
            stage,
            trackingStatus,
            sync: "tracking_exception",
            note: "O transportador reportou uma exceção (avaria/extravio): registre a ocorrência."
        };
    }
    const expected = STAGE_EXPECTED_TRACKING[stage];
    if (expected.includes(trackingStatus)) {
        return { stage, trackingStatus, sync: "aligned", note: "Estado interno e rastreio do transportador coincidem." };
    }
    // Ordena para dizer QUEM está à frente, sem esconder o outro lado.
    const trackingRank = {
        confirmed: 0,
        shipped: 1,
        in_transit: 2,
        delivered: 3,
        exception: 2
    };
    const stageRank = {
        separation: 0,
        checking: 0,
        packing: 0,
        dispatch: 2,
        delivered: 3,
        reverse: 2
    };
    if (trackingRank[trackingStatus] > stageRank[stage]) {
        return {
            stage,
            trackingStatus,
            sync: "tracking_ahead",
            note: "O transportador já avançou além da etapa interna — atualize a expedição."
        };
    }
    return {
        stage,
        trackingStatus,
        sync: "internal_ahead",
        note: "A expedição avançou além do que o rastreio mostra — aguardando o transportador."
    };
}
